import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchGET, fetchPUT } from "../apiHelper";
import { projektnaPomoc } from "../listOfApi";
import DeminimisDetailsForm from "./deminimis/DeminimisDetailsForm";
import DeminimisPageLayout from "./deminimis/DeminimisPageLayout";
import useDeminimisOptions from "./deminimis/useDeminimisOptions";
import {
  buildDetailFormData,
  mapDeminimisDetailResponse,
  normalizeDateToInput,
  parseAmount,
} from "./deminimis/deminimisUtils";
import RoleGuard from "../auth/RoleGuard";
import { MODULES, ROLE_GROUPS } from "../auth/roles";
import AccessDenied from "./ui/AccessDenied";
import useRoleCheck from "../auth/useRoleCheck";
import useClanicaCheck from "../auth/useClanicaCheck";

const DeminimisDetailsPage = ({ role }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const recordId = Number(id);
  const { isFakultetniSkrbnik, isEditor } = useRoleCheck(MODULES.DEMINIMIS);
  const {
    defaultOptions,
    userClanicaId,
    prejemnikDefaultOptions,
    dajalecDefaultOptions,
    fetchPrejemnikOptions,
    fetchDajalecOptions,
    fetchProjektOptions,
  } = useDeminimisOptions(role);
  //TODO Preveriti smiselenost tega
  const isRestrictedToOwnClanica = isFakultetniSkrbnik;

  const [deminimisDetails, setDeminimisDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [detailsSubmitError, setDetailsSubmitError] = useState("");
  const [detailsSubmitSuccess, setDetailsSubmitSuccess] = useState("");
  const [detailsPomocOdobrena, setDetailsPomocOdobrena] = useState(false);

  const [detailsFormData, setDetailsFormData] = useState({
    prejemnikID: "",
    dajalecID: "",
    projektID: "",
    datumNacrtovanePomoci: "",
    nacrtovanaVrednost: "",
    datumOdobritve: "",
    visinaPomoci: "",
    pravnaPodlaga: "",
    stevilkaPriglasitve: "",
  });

  // Check if user is from the same clanica as the 's prejemnik
  const { isUserFromClanica } = useClanicaCheck(deminimisDetails?.prejemnikId);

  // Determine if the user can edit de minimis records (Editors, fakultetni skrbnik of the same clanica)
  const canEditDeminimis =
    isEditor || (isFakultetniSkrbnik && isUserFromClanica);

  const wrappedFetchProjektOptions = useMemo(() => {
    return (searchTerm, oldOptions) => {
      const clanicaId = detailsFormData.prejemnikID || "";
      return fetchProjektOptions(clanicaId, searchTerm, oldOptions);
    };
  }, [detailsFormData.prejemnikID, fetchProjektOptions]);

  const applyLoadedDetails = (response) => {
    const mappedDetails = mapDeminimisDetailResponse(response);
    setDeminimisDetails(mappedDetails);
    setDetailsFormData(buildDetailFormData(mappedDetails));
    setDetailsPomocOdobrena(Boolean(mappedDetails.pomocOdobrena));
    setIsEditingDetails(false);
    setDetailsSubmitError("");
    setDetailsSubmitSuccess("");
  };

  useEffect(() => {
    if (Number.isNaN(recordId)) {
      setDetailsError("ID zapisa ni veljaven.");
      return;
    }

    const loadDetails = async () => {
      setIsLoadingDetails(true);
      setDetailsError("");
      setDeminimisDetails(null);

      const response = await fetchGET(
        `${projektnaPomoc}/${encodeURIComponent(recordId)}`,
      );

      if (
        response === 0 ||
        Array.isArray(response) ||
        typeof response !== "object"
      ) {
        setDetailsError("Napaka pri nalaganju podrobnosti.");
        setIsLoadingDetails(false);
        return;
      }

      applyLoadedDetails(response);
      setIsLoadingDetails(false);
    };

    loadDetails();
  }, [recordId]);

  const handleDetailsFormData = (e) => {
    setDetailsFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const handleDetailsPomocOdobrenaChange = (checked) => {
    setDetailsPomocOdobrena(checked);

    if (!checked) {
      setDetailsFormData((prevState) => ({
        ...prevState,
        datumOdobritve: "",
        visinaPomoci: "",
        pravnaPodlaga: "",
        stevilkaPriglasitve: "",
      }));
    }
  };

  const handleDetailsNacrtovanaVrednostChange = (value) => {
    setDetailsFormData((prevState) => ({
      ...prevState,
      nacrtovanaVrednost: value || "",
    }));
  };

  const handleDetailsVisinaPomociChange = (value) => {
    setDetailsFormData((prevState) => ({
      ...prevState,
      visinaPomoci: value || "",
    }));
  };

  const handleDetailsPrejemnikChange = (selectedValue) => {
    if (isRestrictedToOwnClanica) {
      return;
    }

    setDetailsFormData((prevState) => ({
      ...prevState,
      prejemnikID: selectedValue,
      projektID: "",
    }));
  };

  const handleDetailsDajalecChange = (selectedValue) => {
    setDetailsFormData((prevState) => ({
      ...prevState,
      dajalecID: selectedValue,
    }));
  };

  const handleDetailsProjektChange = (selectedValue, selectedOption) => {
    const normalizedZacetek = normalizeDateToInput(selectedOption?.zacetek);

    setDetailsFormData((prevState) => ({
      ...prevState,
      projektID: selectedValue,
      datumNacrtovanePomoci:
        normalizedZacetek || prevState.datumNacrtovanePomoci,
    }));
  };

  const handleStartEditDetails = () => {
    if (!canEditDeminimis) {
      return;
    }

    setIsEditingDetails(true);
    setDetailsSubmitError("");
    setDetailsSubmitSuccess("");
  };

  const handleCancelEditDetails = () => {
    if (!deminimisDetails) {
      setIsEditingDetails(false);
      return;
    }

    setDetailsFormData(buildDetailFormData(deminimisDetails));
    setDetailsPomocOdobrena(Boolean(deminimisDetails.pomocOdobrena));
    setIsEditingDetails(false);
    setDetailsSubmitError("");
    setDetailsSubmitSuccess("");
  };

  const handleSubmitDetailsForm = async (e) => {
    e.preventDefault();
    setDetailsSubmitError("");
    setDetailsSubmitSuccess("");

    if (!canEditDeminimis) {
      setDetailsSubmitError("Nimate pravic za urejanje de minimis zapisov.");
      return;
    }

    if (isRestrictedToOwnClanica && !isUserFromClanica) {
      setDetailsSubmitError("Urejate lahko samo zapise svoje članice.");
      return;
    }

    if (
      isRestrictedToOwnClanica &&
      (userClanicaId === undefined || userClanicaId === null)
    ) {
      setDetailsSubmitError(
        "Vaša članica ni določena, zato urejanje ni mogoče.",
      );
      return;
    }

    if (Number.isNaN(recordId)) {
      setDetailsSubmitError("ID zapisa ni veljaven.");
      return;
    }

    if (!detailsFormData.prejemnikID) {
      setDetailsSubmitError("Polje Prejemnik državne pomoči je obvezno.");
      return;
    }

    if (!detailsFormData.dajalecID) {
      setDetailsSubmitError("Polje Dajalec je obvezno.");
      return;
    }

    if (!detailsFormData.projektID) {
      setDetailsSubmitError("Polje Projekt je obvezno.");
      return;
    }

    const nacrtovanaVrednost = parseAmount(detailsFormData.nacrtovanaVrednost);
    if (nacrtovanaVrednost === null || nacrtovanaVrednost < 0) {
      setDetailsSubmitError(
        "Načrtovana vrednost mora biti število enako ali večje od 0.",
      );
      return;
    }

    const visinaPomoci = parseAmount(detailsFormData.visinaPomoci);

    if (detailsPomocOdobrena) {
      if (!detailsFormData.datumOdobritve) {
        setDetailsSubmitError("Datum odobritve državne pomoči je obvezen.");
        return;
      }

      if (visinaPomoci === null || visinaPomoci < 0) {
        setDetailsSubmitError(
          "Višina odobrene pomoči mora biti število enako ali večje od 0.",
        );
        return;
      }

      if (visinaPomoci > nacrtovanaVrednost) {
        setDetailsSubmitError(
          "Višina odobrene pomoči ne sme biti večja od načrtovane vrednosti.",
        );
        return;
      }
    }

    const prejemnikId = Number(
      isRestrictedToOwnClanica ? userClanicaId : detailsFormData.prejemnikID,
    );
    const dajalecId = Number(detailsFormData.dajalecID);
    const projektID = Number(detailsFormData.projektID);

    if (
      Number.isNaN(prejemnikId) ||
      Number.isNaN(dajalecId) ||
      Number.isNaN(projektID)
    ) {
      setDetailsSubmitError(
        "Izbran prejemnik, dajalec ali projekt ni veljaven.",
      );
      return;
    }

    const payload = {
      PrejemnikId: prejemnikId,
      DajalecId: dajalecId,
      ProjektID: projektID,
      NacrtovanaVrednost: nacrtovanaVrednost,
      PomocOdobrena: detailsPomocOdobrena,
      DatumNacrtovanePomoci: detailsFormData.datumNacrtovanePomoci,
      DatumOdobritve: detailsPomocOdobrena
        ? detailsFormData.datumOdobritve
        : null,
      VisinaOdobrenePomoci: detailsPomocOdobrena ? visinaPomoci : null,
      PravnaPodlaga: detailsFormData.pravnaPodlaga,
      StevilkaPriglasitve: detailsFormData.stevilkaPriglasitve,
    };

    setIsSavingDetails(true);
    const updateResult = await fetchPUT(
      `${projektnaPomoc}/${encodeURIComponent(recordId)}`,
      payload,
    );

    if (updateResult === 1) {
      setDetailsSubmitSuccess("Podatki so uspešno posodobljeni.");
      const refreshedResponse = await fetchGET(
        `${projektnaPomoc}/${encodeURIComponent(recordId)}`,
      );

      if (
        refreshedResponse !== 0 &&
        !Array.isArray(refreshedResponse) &&
        typeof refreshedResponse === "object"
      ) {
        applyLoadedDetails(refreshedResponse);
        setDetailsSubmitSuccess("Podatki so uspešno posodobljeni.");
      }
    } else {
      setDetailsSubmitError("Prišlo je do napake pri shranjevanju.");
    }

    setIsSavingDetails(false);
  };

  return (
    <RoleGuard
      moduleKey={MODULES.DEMINIMIS}
      roles={ROLE_GROUPS.EDITORS_AND_SKRBNIK}
      fallback={<AccessDenied />}
    >
      <DeminimisPageLayout title="De minimis - podrobnosti" lg={5} md={7}>
        <DeminimisDetailsForm
          detailData={deminimisDetails}
          isLoading={isLoadingDetails}
          loadError={detailsError}
          onBack={() => navigate(-1)}
          canEdit={canEditDeminimis}
          isEditing={isEditingDetails}
          onStartEdit={handleStartEditDetails}
          onCancelEdit={handleCancelEditDetails}
          onSubmit={handleSubmitDetailsForm}
          isSubmitting={isSavingDetails}
          submitError={detailsSubmitError}
          submitSuccess={detailsSubmitSuccess}
          deminimisData={detailsFormData}
          pomocOdobrena={detailsPomocOdobrena}
          onFieldChange={handleDetailsFormData}
          onPomocOdobrenaChange={handleDetailsPomocOdobrenaChange}
          onSelectPrejemnik={handleDetailsPrejemnikChange}
          onSelectDajalec={handleDetailsDajalecChange}
          onSelectProjekt={handleDetailsProjektChange}
          onNacrtovanaVrednostChange={handleDetailsNacrtovanaVrednostChange}
          onVisinaPomociChange={handleDetailsVisinaPomociChange}
          prejemnikDefaultOptions={prejemnikDefaultOptions}
          dajalecDefaultOptions={dajalecDefaultOptions}
          isPrejemnikLocked={isRestrictedToOwnClanica}
          defaultOptions={defaultOptions}
          fetchPrejemnikOptions={fetchPrejemnikOptions}
          fetchDajalecOptions={fetchDajalecOptions}
          fetchProjektOptions={wrappedFetchProjektOptions}
        />
      </DeminimisPageLayout>
    </RoleGuard>
  );
};

export default DeminimisDetailsPage;
